import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { useToast } from '../context/ToastContext';
import { usePlan, LIMITES } from '../context/PlanContext';
import AvatarPremium from '../components/AvatarPremium';
import IntroCarousel from '../components/IntroCarousel';
import { Compass, ArrowLeft, ArrowRight, ArrowUp, X, Search, BookOpen, CheckCircle, Mail, Settings, MapPin } from 'lucide-react';

const SLIDES_EXPLORAR = [
  { Icon: Compass,     titulo: 'Bem-vindo ao Explorar', descricao: 'Aqui você encontra livros disponíveis para empréstimo na sua comunidade. Cada card é um livro real de outro leitor.' },
  { Icon: ArrowLeft,   titulo: 'Não tenho interesse', descricao: 'Deslize para a esquerda (ou toque ✗) para passar. O livro some e você vai para o próximo.' },
  { Icon: ArrowRight,  titulo: 'Quero ler no futuro', descricao: 'Deslize para a direita para salvar na sua lista de leituras futuras — sem pedir emprestado agora.' },
  { Icon: ArrowUp,     titulo: 'Quero pegar emprestado!', descricao: 'Deslize para cima (ou toque ✓) para abrir a solicitação de empréstimo direto com o dono do livro.' },
];

function getLoanStep(emp) {
  if (emp.status === 'devolvido') return 4;
  if (emp.status === 'em_andamento') return 3;
  if (emp.status === 'aprovado' && emp.forma_entrega) return 2;
  if (emp.status === 'aprovado') return 1;
  return 0;
}

const STATUS_LABEL = ['Pedido enviado', 'Aprovado', 'Entrega combinada', 'Com o leitor', 'Devolvido'];

function EmprestimoMiniCard({ livro, emprestimo }) {
  const step = getLoanStep(emprestimo);
  return (
    <div className="p-3 rounded-[14px] bg-white border border-line"
      style={{ boxShadow: '0 1px 2px rgba(33,28,43,0.04)' }}>
      <div className="flex items-center gap-3 mb-[10px]">
        <div className="w-9 h-[52px] rounded-[6px] overflow-hidden flex-shrink-0 flex items-center justify-center"
          style={{ background: '#E8E4F0' }}>
          {livro?.capa_url
            ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
            : <BookOpen size={14} color="#B8B0CC" strokeWidth={1.5} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-bold text-ink-900 truncate m-0">{livro?.titulo}</p>
          <p className="text-[11px] text-ink-400 font-medium truncate m-0">{livro?.autor}</p>
          <p className="text-[11px] font-semibold mt-[3px] m-0" style={{ color: '#5B3CE4' }}>
            {STATUS_LABEL[step]}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-[7px]">
        {[0, 1, 2, 3].map(s => (
          <i key={s} className="flex-1 h-[3px] rounded-full block not-italic"
            style={{ background: s < step ? '#1FA971' : s === step ? '#5B3CE4' : '#F3EFE9' }} />
        ))}
      </div>
    </div>
  );
}

function distanciaKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatarDist(km) {
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1).replace('.', ',')} km`;
}

function MatchCard({ livro, perfil, label, solicitou, onOferecer, onDeclinar, onCancelar }) {
  if (!livro) return null;
  const solicitouStyle = solicitou
    ? { background: '#EDE8FC', borderColor: '#CFC5F7' }
    : { background: '#fff', borderColor: '#EBE8F0' };
  return (
    <div className="flex items-center gap-3 p-3 rounded-[14px] border"
      style={{ boxShadow: '0 1px 2px rgba(33,28,43,0.04)', ...solicitouStyle }}>
      <div className="w-10 h-14 rounded-[7px] overflow-hidden flex-shrink-0 flex items-center justify-center"
        style={{ background: '#E8E4F0' }}>
        {livro.capa_url
          ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
          : <BookOpen size={18} color="#B8B0CC" strokeWidth={1.5} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-bold text-ink-900 truncate">{livro.titulo}</p>
        <p className="text-[12px] text-ink-400 font-medium truncate">{livro.autor}</p>
        {perfil && (
          <div className="flex items-center gap-1 mt-1">
            <span className="text-[11px] text-ink-300 font-medium">{label}:</span>
            <span className="text-[11px] text-ink-500 font-semibold">{perfil.nome}</span>
          </div>
        )}
        {solicitou && (
          <span className="text-[11px] font-semibold mt-0.5 block" style={{ color: '#5B3CE4' }}>
            Solicitação enviada
          </span>
        )}
      </div>
      {onCancelar && (
        <button onClick={onCancelar}
          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer border-0"
          style={{ background: '#F3EFE9', color: '#9A93A4' }}>
          <X size={13} strokeWidth={2.5} />
        </button>
      )}
      {(onOferecer || onDeclinar) && (
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          {onOferecer && (
            <button onClick={onOferecer}
              className="text-[11px] font-bold px-3 py-[7px] rounded-[9px] border-0 cursor-pointer"
              style={{ background: '#5B3CE4', color: '#fff' }}>
              Oferecer
            </button>
          )}
          {onDeclinar && (
            <button onClick={onDeclinar}
              className="text-[11px] font-bold px-3 py-[7px] rounded-[9px] cursor-pointer"
              style={{ background: 'transparent', color: '#9A93A4', border: '1.5px solid #EBE8F0' }}>
              Recusar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const RAIOS = [1, 2, 3, 5, 10, 20, 50, null];

export default function Explorar({ userId, setTela, nomeUsuario }) {
  const { showToast } = useToast();
  const { isPago, openUpgrade } = usePlan();
  const [mostrarIntro, setMostrarIntro] = useState(false);
  const [aba, setAba] = useState('descobrir');
  const [livros, setLivros] = useState([]);
  const [indice, setIndice] = useState(0);
  const [matches, setMatches] = useState({ meus: [], nosMeus: [] });
  const [localidade, setLocalidade] = useState(null);
  const [raio, setRaio] = useState(10);
  const [carregando, setCarregando] = useState(true);
  const [semLocalizacao, setSemLocalizacao] = useState(false);
  const [erroDb, setErroDb] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [flyDir, setFlyDir] = useState(null);
  const [modalSolicitacao, setModalSolicitacao] = useState(null);
  const [prazo, setPrazo] = useState(14);
  const [msgSolicitacao, setMsgSolicitacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [sucessoSolicitacao, setSucessoSolicitacao] = useState(false);
  const [modalDetalhe, setModalDetalhe] = useState(null);
  const [ratingsDonoMap, setRatingsDonoMap] = useState({});
  const [busca, setBusca] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [generoFiltro, setGeneroFiltro] = useState(null);
  const touchStart = useRef(null);

  useEffect(() => {
    if (userId && !localStorage.getItem(`tour_explorar_${userId}`)) {
      setMostrarIntro(true);
    }
  }, [userId]);

  useEffect(() => {
    if (!navigator.geolocation) { setSemLocalizacao(true); setCarregando(false); return; }
    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude: lat, longitude: lng } }) => {
        // Salva primeiro, depois carrega — garante que este usuário aparece para outros
        const { error } = await supabase.from('perfis').update({ lat, lng }).eq('id', userId);
        if (error) {
          console.error('Erro ao salvar localização (rodar migração?):', error.message);
        }
        setLocalidade({ lat, lng });
      },
      () => { setSemLocalizacao(true); setCarregando(false); },
      { timeout: 10000 }
    );
  }, [userId]);

  const fetchMatches = useCallback(async () => {
    const [{ data: meusInt }, { data: nosSeusInt }] = await Promise.all([
      supabase.from('interesses')
        .select('id, livro_id, dono_id, solicitou, livros(id, titulo, autor, capa_url)')
        .eq('user_id', userId).eq('interesse', true)
        .order('created_at', { ascending: false }),
      supabase.from('interesses')
        .select('id, user_id, livro_id, livros(id, titulo, autor, capa_url)')
        .eq('dono_id', userId).eq('interesse', true)
        .order('created_at', { ascending: false }),
    ]);

    const allIds = [...new Set([...(meusInt || []).map(i => i.dono_id), ...(nosSeusInt || []).map(i => i.user_id)])];
    let perfisMap = {};
    if (allIds.length) {
      const { data: p } = await supabase.from('perfis').select('id, nome, foto_url, plano').in('id', allIds);
      (p || []).forEach(x => { perfisMap[x.id] = x; });
    }

    // Buscar emprestimos para itens já solicitados
    const solicitouIds = (meusInt || []).filter(i => i.solicitou).map(i => i.livro_id);
    let emprestimosMap = {};
    if (solicitouIds.length) {
      const { data: emps } = await supabase
        .from('emprestimos')
        .select('id, status, forma_entrega, data_devolucao, livro_id')
        .eq('leitor_user_id', userId)
        .in('livro_id', solicitouIds)
        .not('status', 'eq', 'devolvido');
      (emps || []).forEach(e => { emprestimosMap[e.livro_id] = e; });
    }

    setMatches({
      meus: (meusInt || []).map(i => ({ ...i, perfil: perfisMap[i.dono_id], emprestimo: emprestimosMap[i.livro_id] || null })),
      nosMeus: (nosSeusInt || []).map(i => ({ ...i, perfil: perfisMap[i.user_id] })),
    });
  }, [userId]);

  const fetchLivros = useCallback(async () => {
    if (!localidade) return;
    setCarregando(true);
    setErroDb(false);
    const { data: perfis, error: perfisError } = await supabase
      .from('perfis').select('id, nome, foto_url, lat, lng, plano')
      .not('lat', 'is', null).neq('id', userId);

    if (perfisError) {
      console.error('Erro Explorar (migração pendente?):', perfisError.message);
      setErroDb(true);
      setCarregando(false);
      return;
    }

    if (!perfis?.length) { setLivros([]); setCarregando(false); return; }

    const perfisMap = {};
    const proximos = perfis.filter(p => {
      if (!p.lat || !p.lng) return false;
      const d = distanciaKm(localidade.lat, localidade.lng, p.lat, p.lng);
      if (raio === null || d <= raio) { perfisMap[p.id] = { ...p, distancia: d }; return true; }
      return false;
    });

    if (!proximos.length) { setLivros([]); setCarregando(false); return; }

    const { data: jaVistos } = await supabase
      .from('interesses').select('livro_id').eq('user_id', userId);
    const jaVistosSet = new Set((jaVistos || []).map(i => i.livro_id));

    const [livrosResult, { data: meuPerfil }] = await Promise.all([
      supabase.from('livros').select('id, titulo, autor, capa_url, estrelas, user_id, descricao, genero')
        .in('user_id', proximos.map(p => p.id)).eq('disponivel', true).neq('especial', true),
      supabase.from('perfis').select('generos_favoritos').eq('id', userId).maybeSingle(),
    ]);
    // Se a coluna genero não existir ainda no Supabase, cai no fallback sem ela
    const livrosData = livrosResult.error
      ? (await supabase.from('livros').select('id, titulo, autor, capa_url, estrelas, user_id, descricao')
          .in('user_id', proximos.map(p => p.id)).eq('disponivel', true).neq('especial', true)).data
      : livrosResult.data;
    const meuGeneros = meuPerfil?.generos_favoritos || [];

    const filtrados = (livrosData || [])
      .filter(l => !jaVistosSet.has(l.id))
      .map(l => ({ ...l, perfil: perfisMap[l.user_id] }))
      .filter(l => l.perfil)
      .sort((a, b) => {
        const aMatch = meuGeneros.includes(a.genero) ? 1 : 0;
        const bMatch = meuGeneros.includes(b.genero) ? 1 : 0;
        if (bMatch !== aMatch) return bMatch - aMatch;
        return a.perfil.distancia - b.perfil.distancia;
      });

    setLivros(filtrados);
    setIndice(0);
    setCarregando(false);

    // Buscar ratings dos donos para os livros carregados
    if (filtrados.length > 0) {
      const livroToOwner = {};
      filtrados.forEach(l => { livroToOwner[l.id] = l.user_id; });
      const { data: empRatings } = await supabase
        .from('emprestimos')
        .select('livro_id, avaliacao_dono_estrelas')
        .in('livro_id', filtrados.map(l => l.id))
        .gt('avaliacao_dono_estrelas', 0);
      const rMap = {};
      (empRatings || []).forEach(r => {
        const uid = livroToOwner[r.livro_id];
        if (!uid) return;
        if (!rMap[uid]) rMap[uid] = { soma: 0, total: 0 };
        rMap[uid].soma += r.avaliacao_dono_estrelas;
        rMap[uid].total++;
      });
      const ratingsFinais = {};
      Object.entries(rMap).forEach(([uid, { soma, total }]) => {
        ratingsFinais[uid] = { media: (soma / total).toFixed(1), total };
      });
      setRatingsDonoMap(ratingsFinais);
    }
  }, [localidade, raio, userId]);

  useEffect(() => {
    if (localidade) { fetchLivros(); fetchMatches(); }
  }, [localidade, fetchLivros, fetchMatches]);

  useEffect(() => {
    const q = busca.trim();
    if (q.length < 2) { setResultadosBusca([]); return; }
    const timer = setTimeout(async () => {
      setBuscando(true);
      const { data } = await supabase
        .from('livros')
        .select('id, titulo, autor, capa_url, user_id, perfis(id, nome, foto_url, slug, lat, lng, plano)')
        .or(`titulo.ilike.%${q}%,autor.ilike.%${q}%`)
        .eq('disponivel', true)
        .neq('especial', true)
        .neq('user_id', userId)
        .order('titulo')
        .limit(30);
      setResultadosBusca((data || []).map(l => {
        const pf = l.perfis || null;
        const dist = pf && localidade && pf.lat ? distanciaKm(localidade.lat, localidade.lng, pf.lat, pf.lng) : null;
        return { ...l, perfil: pf ? { ...pf, distancia: dist } : null };
      }));
      setBuscando(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [busca, userId, localidade]); // eslint-disable-line react-hooks/exhaustive-deps

  async function acaoCard(dir) {
    const livro = livros[indice];
    if (!livro || flyDir) return;

    if (!isPago) {
      const hoje = new Date().toISOString().slice(0, 10);
      const key = `exp_${userId}_${hoje}`;
      const count = parseInt(localStorage.getItem(key) || '0');
      if (count >= LIMITES.explorar_dia) { openUpgrade('explorar'); return; }
      localStorage.setItem(key, String(count + 1));
    }

    if (dir === 'up') {
      setDragX(0);
      setDragY(0);
      setModalSolicitacao(livro);
      return;
    }

    setFlyDir(dir);
    setDragX(dir === 'right' ? 500 : -500);
    setDragY(0);

    await supabase.from('interesses').upsert(
      { user_id: userId, livro_id: livro.id, dono_id: livro.user_id, interesse: dir !== 'left' },
      { onConflict: 'user_id,livro_id' }
    );

    if (dir === 'right') {
      await supabase.from('notificacoes').insert({
        user_id: livro.user_id, tipo: 'novo_interesse', livro_id: livro.id, lida: false,
        meta: { livro_titulo: livro.titulo, leitor_nome: nomeUsuario || 'Alguém', leitor_id: userId },
      });
      fetchMatches();
      showToast('Adicionado às futuras leituras!', 'success');
    }

    setTimeout(() => { setFlyDir(null); setDragX(0); setDragY(0); setIndice(i => i + 1); }, 350);
  }

  async function confirmarSolicitacao() {
    const livro = modalSolicitacao;
    if (!livro || enviando) return;
    const { data: perfil } = await supabase.from('perfis').select('emprestimo_bloqueado').eq('id', userId).maybeSingle();
    if (perfil?.emprestimo_bloqueado) {
      showToast('Voce tem um emprestimo em atraso. Devolva o livro antes de solicitar um novo.', 'error');
      return;
    }
    if (!isPago) {
      const { count } = await supabase.from('emprestimos').select('*', { count: 'exact', head: true })
        .eq('leitor_user_id', userId).in('status', ['pendente', 'aprovado', 'em_andamento']);
      if (count >= LIMITES.emprestimos_ativos) { openUpgrade('emprestimos'); return; }
    }
    setEnviando(true);
    setFlyDir('up');
    setDragX(0);
    setDragY(-600);

    // Cria emprestimo primeiro para ter o ID antes de criar a notificação
    const { data: empData } = await supabase.from('emprestimos').insert({
      livro_id: livro.id, leitor_user_id: userId, status: 'pendente',
      mensagem: msgSolicitacao || null, prazo_dias: prazo,
    }).select('id').single();

    await Promise.all([
      supabase.from('interesses').upsert(
        { user_id: userId, livro_id: livro.id, dono_id: livro.user_id, interesse: true, solicitou: true, prazo_dias: prazo, mensagem: msgSolicitacao || null },
        { onConflict: 'user_id,livro_id' }
      ),
      supabase.from('notificacoes').insert({
        user_id: livro.user_id, tipo: 'nova_solicitacao', livro_id: livro.id, lida: false,
        emprestimo_id: empData?.id,
        meta: { livro_titulo: livro.titulo, leitor_nome: nomeUsuario || 'Alguém' },
      }),
    ]);

    fetchMatches();
    setEnviando(false);
    setSucessoSolicitacao(true);
    setPrazo(14);
    setMsgSolicitacao('');
    setTimeout(() => { setFlyDir(null); setDragX(0); setDragY(0); setIndice(i => i + 1); }, 350);
  }

  async function handleOferecer(interesse) {
    await supabase.from('notificacoes').insert({
      user_id: interesse.user_id,
      tipo: 'proposta_dono',
      livro_id: interesse.livro_id,
      lida: false,
      meta: {
        livro_titulo: interesse.livros?.titulo || '',
        livro_id: interesse.livro_id,
        dono_id: userId,
        dono_nome: nomeUsuario || 'O dono',
      },
    });
    showToast('Proposta enviada!', 'success');
  }

  async function handleCancelarInteresse(interesse) {
    await supabase.from('interesses').delete().eq('id', interesse.id);
    fetchMatches();
  }

  async function handleDeclinarInteresse(interesse) {
    await supabase.from('interesses').delete().eq('id', interesse.id);
    fetchMatches();
  }

  function onTouchStart(e) {
    if (flyDir) return;
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    setIsDragging(true);
  }
  function onTouchMove(e) {
    if (!touchStart.current || flyDir) return;
    setDragX(e.touches[0].clientX - touchStart.current.x);
    setDragY(e.touches[0].clientY - touchStart.current.y);
  }
  function onTouchEnd() {
    setIsDragging(false);
    if (!touchStart.current || flyDir) return;
    const dx = dragX, dy = dragY;
    touchStart.current = null;
    if (dy < -100 && Math.abs(dx) < 60) acaoCard('up');
    else if (dx > 80) acaoCard('right');
    else if (dx < -80) acaoCard('left');
    else if (Math.abs(dx) < 10 && Math.abs(dy) < 10 && cardAtual) {
      setDragX(0); setDragY(0);
      setModalDetalhe(cardAtual);
    }
    else { setDragX(0); setDragY(0); }
  }

  function onMouseDown(e) {
    if (flyDir) return;
    touchStart.current = { x: e.clientX, y: e.clientY };
    setIsDragging(true);
  }
  function onMouseMove(e) {
    if (!isDragging || !touchStart.current || flyDir) return;
    setDragX(e.clientX - touchStart.current.x);
    setDragY(e.clientY - touchStart.current.y);
  }
  function onMouseUp() { onTouchEnd(); }

  const cardAtual = livros[indice];
  const proxCard = livros[indice + 1];
  const rightOp = Math.max(0, Math.min(dragX / 80, 1));
  const leftOp = Math.max(0, Math.min(-dragX / 80, 1));
  const upOp = dragY < 0 ? Math.max(0, Math.min(-dragY / 80, 1)) : 0;
  const totalMatches = matches.meus.length + matches.nosMeus.length;

  const frosted = {
    background: 'rgba(255,255,255,0.82)',
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    borderTop: '1px solid #F2EEE9',
  };

  function fecharIntroExplorar() {
    localStorage.setItem(`tour_explorar_${userId}`, 'done');
    setMostrarIntro(false);
  }

  return (
    <div className="bg-paper font-sans flex flex-col lg:flex-row select-none" style={{height:'100dvh'}}
      onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}>

      {mostrarIntro && <IntroCarousel slides={SLIDES_EXPLORAR} onClose={fecharIntroExplorar} />}

      {/* Left column — card stack and controls */}
      <div className="flex flex-col flex-1 min-h-0 lg:border-r lg:border-line-soft lg:overflow-hidden">

      <div className="lg:hidden" style={{ height: 'env(safe-area-inset-top)', minHeight: 20 }} />

      {/* Header — mobile only */}
      <div className="px-5 pt-3 pb-3 lg:hidden">
        <h1 className="text-[22px] font-extrabold tracking-tight text-ink-900 mb-3">Explorar</h1>

        {/* Search box */}
        <div className="relative mb-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{color:'#BDB7C5'}} />
          <input
            type="text"
            placeholder="Qual livro, ou autor, você quer ler?"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            className="w-full bg-white border border-line rounded-[13px] pl-9 pr-8 py-2.5 text-[13.5px] outline-none text-ink-900 placeholder-ink-300"
            style={{focusBorderColor:'#5B3CE4'}}
          />
          {busca && (
            <button onClick={() => setBusca('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X size={14} style={{color:'#9A93A4'}} />
            </button>
          )}
        </div>

        {/* Distance pills — hidden when searching */}
        {!busca && (
          <div className="flex gap-1.5 overflow-x-auto" style={{scrollbarWidth:'none', WebkitOverflowScrolling:'touch'}}>
            {RAIOS.map(r => (
              <button key={r ?? 'tudo'} onClick={() => setRaio(r)}
                className="flex-shrink-0 px-3 py-[5px] rounded-full text-[11.5px] font-bold border transition-all"
                style={raio === r
                  ? {background:'#5B3CE4', color:'#fff', borderColor:'#5B3CE4'}
                  : {background:'transparent', color:'#9A93A4', borderColor:'#ECE7E1'}}>
                {r === null ? 'Tudo' : `${r}km`}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tab toggle — mobile only, hidden when searching */}
      {!busca && <div className="flex gap-1 mx-5 mb-3 p-[5px] rounded-[14px] lg:hidden" style={{ background: '#F2EEE9' }}>
        {[['descobrir', 'Descobrir'], ['matches', 'Futuras Leituras']].map(([v, l]) => (
          <button key={v} onClick={() => { setAba(v); if (v === 'matches') fetchMatches(); }}
            className="flex-1 py-[8px] rounded-[10px] text-[13px] font-bold transition-all"
            style={aba === v
              ? { background: '#5B3CE4', color: '#fff', boxShadow: '0 2px 6px rgba(91,60,228,0.30)' }
              : { background: 'transparent', color: '#9A93A4' }}>
            {l}
            {v === 'matches' && totalMatches > 0 && (
              <span className="ml-1.5 text-[10px] font-bold px-[5px] py-[1px] rounded-full align-middle"
                style={{ background: '#5B3CE4', color: '#fff' }}>
                {totalMatches}
              </span>
            )}
          </button>
        ))}
      </div>}

      {/* Content — mobile only */}
      <div className="flex-1 flex flex-col min-h-0 pb-[84px] lg:hidden">
        {busca.trim().length >= 2 ? (
          /* Search results */
          <div className="flex-1 overflow-y-auto px-5 pt-2" style={{scrollbarWidth:'none'}}>
            {buscando ? (
              <p className="text-center text-[13px] text-ink-400 pt-12 animate-pulse">Buscando…</p>
            ) : resultadosBusca.length === 0 ? (
              <div className="text-center pt-16">
                <p className="text-[15px] font-semibold text-ink-900 mb-1">Nenhum resultado</p>
                <p className="text-[13px] text-ink-400">Tente outro título ou nome de autor.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2.5">
                <p className="text-[11.5px] font-bold text-ink-400 uppercase tracking-wider mb-0.5">
                  {resultadosBusca.length} resultado{resultadosBusca.length !== 1 ? 's' : ''}
                </p>
                {resultadosBusca.map(livro => (
                  <div key={livro.id} className="flex items-center gap-3 bg-white rounded-[14px] p-3 border border-line"
                    style={{boxShadow:'0 1px 4px rgba(33,28,43,0.06)'}}>
                    <div className="w-11 h-15 rounded-[8px] overflow-hidden flex-shrink-0 flex items-center justify-center"
                      style={{width:44, height:62, background:'#E8E4F0'}}>
                      {livro.capa_url
                        ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
                        : <BookOpen size={18} color="#9A93A4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13.5px] font-bold text-ink-900 leading-snug truncate">{livro.titulo}</p>
                      <p className="text-[12px] text-ink-400 truncate">{livro.autor}</p>
                      {livro.perfil && (
                        <p className="text-[11.5px] text-ink-400 mt-0.5">
                          {livro.perfil.nome}
                          {livro.perfil.distancia != null && ` · ${formatarDist(livro.perfil.distancia)}`}
                        </p>
                      )}
                    </div>
                    <button onClick={() => setModalSolicitacao(livro)}
                      className="flex-shrink-0 px-3 py-2 rounded-[10px] text-[12px] font-bold text-white"
                      style={{background:'#5B3CE4'}}>
                      Pedir
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : aba === 'descobrir' ? (
          semLocalizacao ? (
            <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-3">
              <div className="w-16 h-16 rounded-[22px] flex items-center justify-center" style={{background:'#EDE8FC'}}>
                <MapPin size={30} strokeWidth={1.5} color="#5B3CE4" />
              </div>
              <p className="text-[16px] font-bold text-ink-900">Localização necessária</p>
              <p className="text-[13px] text-ink-400 font-medium leading-relaxed">
                Permita o acesso à localização para ver livros disponíveis perto de você.
              </p>
            </div>
          ) : erroDb ? (
            <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-3">
              <div className="w-16 h-16 rounded-[22px] flex items-center justify-center" style={{background:'#EDE8FC'}}>
                <Settings size={30} strokeWidth={1.5} color="#5B3CE4" />
              </div>
              <p className="text-[16px] font-bold text-ink-900">Configuração pendente</p>
              <p className="text-[13px] text-ink-400 font-medium leading-relaxed">
                As migrações do banco ainda não foram aplicadas. Rode o SQL no Supabase para ativar o Explorar.
              </p>
            </div>
          ) : carregando ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-[14px] text-ink-400 font-medium animate-pulse">Buscando livros próximos…</p>
            </div>
          ) : !cardAtual ? (
            <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-3">
              <div className="w-16 h-16 rounded-[22px] flex items-center justify-center"
                style={{background:'#EDE8FC'}}>
                <Compass size={30} strokeWidth={1.5} color="#5B3CE4" />
              </div>
              <div>
                <p className="text-[16px] font-bold text-ink-900 mb-1">Nenhum livro por aqui</p>
                <p className="text-[13px] text-ink-400 font-medium leading-relaxed">
                  {raio === null
                    ? 'Não há livros disponíveis no momento.'
                    : `Não encontramos livros em até ${raio} km. Tente ampliar o raio.`}
                </p>
              </div>
              {raio === null && (
                <p className="text-[12px] text-ink-400 font-medium">
                  Convide amigos para se cadastrar!
                </p>
              )}
            </div>
          ) : (
            /* Card stack — fills all remaining space */
            <div className="relative flex-1 min-h-0 mx-5">
              {proxCard && (
                <div className="absolute inset-0 rounded-[24px] overflow-hidden"
                  style={{ transform: 'scale(0.94) translateY(12px)', boxShadow: '0 4px 24px rgba(33,28,43,0.12)', zIndex: 1 }}>
                  <div className="w-full h-full" style={{ background: '#E8E4F0' }}>
                    {proxCard.capa_url
                      ? <img src={proxCard.capa_url} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><BookOpen size={48} color="#C4BCDA" strokeWidth={1} /></div>}
                  </div>
                </div>
              )}

              <div className="absolute inset-0 rounded-[24px] overflow-hidden cursor-grab active:cursor-grabbing"
                style={{
                  transform: `translateX(${dragX}px) translateY(${dragY}px) rotate(${dragX * 0.05}deg)`,
                  transition: flyDir ? 'transform 0.35s cubic-bezier(0.25,0.46,0.45,0.94)' : isDragging ? 'none' : 'transform 0.2s ease',
                  boxShadow: '0 4px 24px rgba(33,28,43,0.14)',
                  zIndex: 2,
                }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onMouseDown={onMouseDown}
              >
                {/* Cover */}
                <div className="absolute inset-0" style={{ background: '#E8E4F0' }}>
                  {cardAtual.capa_url
                    ? <img src={cardAtual.capa_url} alt={cardAtual.titulo} className="w-full h-full object-cover" draggable={false} />
                    : <div className="w-full h-full flex items-center justify-center"><BookOpen size={56} color="#C4BCDA" strokeWidth={1} /></div>}
                </div>

                {/* Counter — top right */}
                <div style={{ position: 'absolute', top: 14, right: 14, background: 'rgba(0,0,0,0.35)', borderRadius: 20, padding: '4px 10px', zIndex: 5 }}>
                  <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>
                    {livros.length - indice} disponíve{livros.length - indice !== 1 ? 'is' : 'l'}
                  </span>
                </div>

                {/* Bottom gradient — paddingBottom leaves room for action buttons */}
                <div className="absolute inset-x-0 bottom-0 px-4 pt-14"
                  style={{ paddingBottom: 76, background: 'linear-gradient(to top, rgba(20,16,30,0.96) 0%, rgba(20,16,30,0.55) 40%, transparent 100%)' }}>
                  <p className="text-white text-[21px] font-bold leading-snug mb-1 line-clamp-2"
                    style={{ fontFamily: "'Newsreader', Georgia, serif" }}>
                    {cardAtual.titulo}
                  </p>
                  <p className="text-white/70 text-[13px] font-medium mb-3">{cardAtual.autor}</p>
                  <div className="flex items-center gap-2">
                    <AvatarPremium fotoUrl={cardAtual.perfil?.foto_url} nome={cardAtual.perfil?.nome} isPremium={cardAtual.perfil?.plano === 'pago'} size={24} />
                    <span className="text-white/80 text-[12px] font-semibold truncate">{cardAtual.perfil?.nome}</span>
                    {ratingsDonoMap[cardAtual.user_id] && (
                      <span className="flex items-center gap-0.5 text-[11px] font-bold flex-shrink-0" style={{color:'#FFCA28'}}>
                        ★ {ratingsDonoMap[cardAtual.user_id].media}
                      </span>
                    )}
                    <span className="text-white/50 text-[11px] font-medium ml-auto flex-shrink-0">
                      {formatarDist(cardAtual.perfil?.distancia || 0)}
                    </span>
                  </div>
                </div>

                {/* Swipe indicators */}
                {rightOp > 0.05 && (
                  <div className="absolute inset-0 rounded-[24px] flex items-center justify-start pl-6"
                    style={{ background: `rgba(31,169,113,${rightOp * 0.3})`, pointerEvents: 'none' }}>
                    <div className="w-14 h-14 rounded-full flex items-center justify-center border-[3px] border-[#1FA971]"
                      style={{ opacity: rightOp }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1FA971" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                    </div>
                  </div>
                )}
                {leftOp > 0.05 && (
                  <div className="absolute inset-0 rounded-[24px] flex items-center justify-end pr-6"
                    style={{ background: `rgba(217,79,46,${leftOp * 0.3})`, pointerEvents: 'none' }}>
                    <div className="w-14 h-14 rounded-full flex items-center justify-center border-[3px] border-[#D94F2E]"
                      style={{ opacity: leftOp }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D94F2E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </div>
                  </div>
                )}
                {upOp > 0.05 && (
                  <div className="absolute inset-0 rounded-[24px] flex items-end justify-center pb-[76px]"
                    style={{ background: `rgba(91,60,228,${upOp * 0.3})`, pointerEvents: 'none' }}>
                    <div className="w-14 h-14 rounded-full flex items-center justify-center border-[3px] border-[#5B3CE4]"
                      style={{ opacity: upOp }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5B3CE4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>

              {/* Action buttons — overlaid inside card container, outside draggable */}
              <div style={{ position: 'absolute', bottom: 14, left: 0, right: 0, display: 'flex', flexDirection: 'row', paddingLeft: 14, paddingRight: 14, gap: 10, zIndex: 10 }}>
                <button onClick={() => acaoCard('left')}
                  style={{ flex: 1, height: 48, borderRadius: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'rgba(0,0,0,0.28)', border: 'none', cursor: 'pointer' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>Passar</span>
                </button>
                <button onClick={() => acaoCard('up')}
                  style={{ flex: 1.4, height: 48, borderRadius: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'rgba(0,0,0,0.28)', border: 'none', cursor: 'pointer' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
                  </svg>
                  <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>Pedir</span>
                </button>
                <button onClick={() => acaoCard('right')}
                  style={{ flex: 1, height: 48, borderRadius: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'rgba(0,0,0,0.28)', border: 'none', cursor: 'pointer' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                  <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>Interesse</span>
                </button>
              </div>
            </div>
          )
        ) : (
          /* Matches */
          <div className="flex-1 overflow-y-auto px-5 pb-4" style={{ scrollbarWidth: 'none' }}>
            {matches.meus.length === 0 && matches.nosMeus.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center gap-3 pt-20">
                <div className="w-16 h-16 rounded-[22px] flex items-center justify-center" style={{background:'#EDE8FC'}}>
                  <Mail size={30} strokeWidth={1.5} color="#5B3CE4" />
                </div>
                <p className="text-[16px] font-bold text-ink-900">Nenhuma leitura futura ainda</p>
                <p className="text-[13px] text-ink-400 font-medium">Explore livros e deslize para a direita nos que quiser ler.</p>
              </div>
            ) : (
              <>
                {matches.meus.length > 0 && (
                  <div className="mb-5">
                    <p className="text-[12px] font-bold text-ink-400 mb-3 uppercase tracking-wider">Quero ler</p>
                    <div className="flex flex-col gap-2">
                      {matches.meus.map(m => (
                        m.solicitou && m.emprestimo
                          ? <EmprestimoMiniCard key={m.id} livro={m.livros} emprestimo={m.emprestimo} />
                          : <MatchCard key={m.id} livro={m.livros} perfil={m.perfil} label="Disponível em" solicitou={m.solicitou}
                              onCancelar={() => handleCancelarInteresse(m)} />
                      ))}
                    </div>
                  </div>
                )}
                {matches.nosMeus.length > 0 && (
                  <div>
                    <p className="text-[12px] font-bold text-ink-400 mb-3 uppercase tracking-wider">Querem os meus</p>
                    <div className="flex flex-col gap-2">
                      {matches.nosMeus.map(m => (
                        <MatchCard key={m.id} livro={m.livros} perfil={m.perfil} label="Interesse de"
                          onOferecer={() => handleOferecer(m)}
                          onDeclinar={() => handleDeclinarInteresse(m)} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Desktop grid — hidden on mobile */}
      {(() => {
        const busca2 = busca.trim().toLowerCase();
        const generosUnicos = [...new Set(livros.map(l => l.genero).filter(Boolean))].slice(0, 4);
        const livrosFiltradosTodos = livros.filter(l =>
          (!busca2 || l.titulo?.toLowerCase().includes(busca2) || l.autor?.toLowerCase().includes(busca2)) &&
          (!generoFiltro || l.genero === generoFiltro)
        );
        const livrosFiltrados = isPago ? livrosFiltradosTodos : livrosFiltradosTodos.slice(0, LIMITES.explorar_dia);
        const temMaisLivros = !isPago && livrosFiltradosTodos.length > LIMITES.explorar_dia;
        return (
          <div className="hidden lg:flex flex-col flex-1 overflow-y-auto" style={{scrollbarWidth:'none'}}>
            {/* Desktop header */}
            <div style={{padding:'32px 40px 0'}}>
              <p style={{fontSize:11, fontWeight:800, letterSpacing:'.15em', textTransform:'uppercase', color:'#5B3CE4', margin:0, marginBottom:8}}>Explorar</p>
              <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:6}}>
                <h1 style={{fontSize:36, fontWeight:800, letterSpacing:'-.03em', color:'#211C2B', margin:0, lineHeight:1.1}}>
                  {aba === 'matches' ? 'Futuras Leituras' : 'Livros disponíveis perto de você'}
                </h1>
              </div>
              {/* Tabs */}
              <div style={{display:'flex', gap:4, background:'#F2EEE9', borderRadius:12, padding:4, marginBottom:20, width:'fit-content'}}>
                {[['descobrir','Descobrir'],['matches','Futuras Leituras']].map(([v,l]) => (
                  <button key={v} onClick={() => { setAba(v); if (v === 'matches') fetchMatches(); }}
                    style={{padding:'7px 18px', borderRadius:9, border:'none', cursor:'pointer', fontSize:13, fontWeight:700, transition:'all .15s',
                      background: aba === v ? '#5B3CE4' : 'transparent',
                      color: aba === v ? '#fff' : '#9A93A4',
                      boxShadow: aba === v ? '0 2px 6px rgba(91,60,228,0.25)' : 'none'}}>
                    {l}
                    {v === 'matches' && totalMatches > 0 && (
                      <span style={{marginLeft:6, background: aba === v ? 'rgba(255,255,255,0.3)' : '#5B3CE4', color:'#fff', borderRadius:10, fontSize:11, fontWeight:800, padding:'1px 6px'}}>{totalMatches}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Search — só no Descobrir */}
              {aba === 'descobrir' && (
                <>
                  <div style={{position:'relative', marginBottom:16}}>
                    <Search size={15} style={{position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', color:'#BDB7C5'}} />
                    <input
                      type="text"
                      placeholder="Buscar título, autor ou gênero..."
                      value={busca}
                      onChange={e => setBusca(e.target.value)}
                      style={{width:'100%', background:'#fff', border:'1px solid #EDE9F5', borderRadius:14, paddingLeft:42, paddingRight:14, paddingTop:12, paddingBottom:12, fontSize:14, outline:'none', color:'#211C2B', boxSizing:'border-box'}}
                    />
                    {busca && (
                      <button onClick={() => setBusca('')} style={{position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', background:'transparent', border:'none', cursor:'pointer', padding:0}}>
                        <X size={14} style={{color:'#9A93A4'}} />
                      </button>
                    )}
                  </div>
                  {generosUnicos.length > 0 && (
                    <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom:8}}>
                      <button onClick={() => setGeneroFiltro(null)}
                        style={{padding:'6px 16px', borderRadius:999, fontSize:13, fontWeight:700, border:'1.5px solid', cursor:'pointer', transition:'all .15s',
                          borderColor: generoFiltro === null ? '#5B3CE4' : '#ECE7E1',
                          background: generoFiltro === null ? '#5B3CE4' : 'transparent',
                          color: generoFiltro === null ? '#fff' : '#9A93A4'}}>
                        Todos
                      </button>
                      {generosUnicos.map(g => (
                        <button key={g} onClick={() => setGeneroFiltro(g === generoFiltro ? null : g)}
                          style={{padding:'6px 16px', borderRadius:999, fontSize:13, fontWeight:700, border:'1.5px solid', cursor:'pointer', transition:'all .15s',
                            borderColor: generoFiltro === g ? '#5B3CE4' : '#ECE7E1',
                            background: generoFiltro === g ? '#5B3CE4' : 'transparent',
                            color: generoFiltro === g ? '#fff' : '#9A93A4'}}>
                          {g}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Descobrir — book grid */}
            {aba === 'descobrir' && (
              carregando ? (
                <div style={{padding:'60px 40px', textAlign:'center', color:'#9A93A4', fontSize:14}}>Buscando livros próximos…</div>
              ) : livrosFiltrados.length === 0 ? (
                <div style={{padding:'60px 40px', textAlign:'center', color:'#9A93A4', fontSize:14}}>Nenhum livro encontrado.</div>
              ) : (
                <div style={{padding:'24px 40px 40px', display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:24}}>
                  {livrosFiltrados.map(livro => {
                    const selecionado = modalDetalhe?.id === livro.id;
                    return (
                      <div key={livro.id} onClick={() => setModalDetalhe(selecionado ? null : livro)}
                        style={{cursor:'pointer', borderRadius:14, outline: selecionado ? '2.5px solid #5B3CE4' : '2.5px solid transparent', outlineOffset:3}}>
                        <div style={{aspectRatio:'2/3', borderRadius:12, overflow:'hidden', background:'#E8E4F0', marginBottom:10, boxShadow:'0 2px 12px rgba(33,28,43,0.10)', position:'relative'}}>
                          {livro.capa_url
                            ? <img src={livro.capa_url} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                            : (
                              <>
                                <div style={{position:'absolute', inset:0, background:'linear-gradient(160deg,#3D3068,#1E1A35)'}} />
                                <div style={{position:'absolute', bottom:0, left:0, right:0, padding:'12px 12px 14px', background:'linear-gradient(to top,rgba(20,16,30,.95),transparent)'}}>
                                  <p style={{color:'#fff', fontSize:13, fontWeight:700, margin:0, lineHeight:1.3, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>{livro.titulo}</p>
                                  <p style={{color:'rgba(255,255,255,.65)', fontSize:11, margin:'3px 0 0'}}>{livro.autor?.split(',')[0]?.trim()}</p>
                                </div>
                              </>
                            )}
                        </div>
                        <p style={{fontSize:13, fontWeight:700, color:'#211C2B', margin:0, lineHeight:1.25, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>{livro.titulo}</p>
                        <p style={{fontSize:12, color:'#9A93A4', margin:'3px 0 5px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{livro.autor?.split(',')[0]?.trim()}</p>
                        <div style={{display:'flex', alignItems:'center', gap:6}}>
                          <AvatarPremium fotoUrl={livro.perfil?.foto_url} nome={livro.perfil?.nome} isPremium={livro.perfil?.plano === 'pago'} size={18} />
                          <span style={{fontSize:12, color:'#9A93A4', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                            {livro.perfil?.nome?.split(' ')[0]} · {formatarDist(livro.perfil?.distancia || 0)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {temMaisLivros && (
                    <div onClick={() => openUpgrade('explorar')}
                      style={{cursor:'pointer', borderRadius:14}}>
                      <div style={{aspectRatio:'2/3', borderRadius:12, background:'linear-gradient(135deg,#EDE8FC,#D4C8F8)', marginBottom:10, boxShadow:'0 2px 12px rgba(33,28,43,0.10)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10, padding:'16px 12px', textAlign:'center'}}>
                        <div style={{width:44, height:44, borderRadius:14, background:'#5B3CE4', display:'flex', alignItems:'center', justifyContent:'center'}}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                        </div>
                        <p style={{fontSize:13, fontWeight:800, color:'#5B3CE4', margin:0, lineHeight:1.3}}>+{livrosFiltradosTodos.length - LIMITES.explorar_dia} livros</p>
                        <p style={{fontSize:11, color:'#7C5DEF', margin:0, lineHeight:1.4}}>Faça upgrade para ver todos os livros disponíveis</p>
                      </div>
                      <p style={{fontSize:13, fontWeight:700, color:'#5B3CE4', margin:0}}>Ver todos os livros</p>
                      <p style={{fontSize:12, color:'#9A93A4', margin:'3px 0 0'}}>Plano Premium</p>
                    </div>
                  )}
                </div>
              )
            )}

            {/* Futuras Leituras — desktop */}
            {aba === 'matches' && (
              <div style={{padding:'8px 40px 40px'}}>
                {matches.meus.length === 0 && matches.nosMeus.length === 0 ? (
                  <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', gap:12, paddingTop:80}}>
                    <div style={{width:64, height:64, borderRadius:22, background:'#EDE8FC', display:'flex', alignItems:'center', justifyContent:'center'}}>
                      <Mail size={30} strokeWidth={1.5} color="#5B3CE4" />
                    </div>
                    <p style={{fontSize:16, fontWeight:800, color:'#211C2B', margin:0}}>Nenhuma leitura futura ainda</p>
                    <p style={{fontSize:14, color:'#9A93A4', margin:0}}>Explore livros e salve os que quiser ler depois.</p>
                    <button onClick={() => setAba('descobrir')} style={{marginTop:8, padding:'10px 24px', borderRadius:12, background:'#5B3CE4', color:'#fff', fontWeight:700, fontSize:14, border:'none', cursor:'pointer'}}>
                      Explorar livros
                    </button>
                  </div>
                ) : (
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:32, maxWidth:800}}>
                    {matches.meus.length > 0 && (
                      <div>
                        <p style={{fontSize:11, fontWeight:800, letterSpacing:'.12em', textTransform:'uppercase', color:'#9A93A4', margin:'0 0 12px'}}>Quero ler</p>
                        <div style={{display:'flex', flexDirection:'column', gap:8}}>
                          {matches.meus.map(m => (
                            m.solicitou && m.emprestimo
                              ? <EmprestimoMiniCard key={m.id} livro={m.livros} emprestimo={m.emprestimo} />
                              : <MatchCard key={m.id} livro={m.livros} perfil={m.perfil} label="Disponível em" solicitou={m.solicitou}
                                  onCancelar={() => handleCancelarInteresse(m)} />
                          ))}
                        </div>
                      </div>
                    )}
                    {matches.nosMeus.length > 0 && (
                      <div>
                        <p style={{fontSize:11, fontWeight:800, letterSpacing:'.12em', textTransform:'uppercase', color:'#9A93A4', margin:'0 0 12px'}}>Querem os meus</p>
                        <div style={{display:'flex', flexDirection:'column', gap:8}}>
                          {matches.nosMeus.map(m => (
                            <MatchCard key={m.id} livro={m.livros} perfil={m.perfil} label="Interesse de"
                              onOferecer={() => handleOferecer(m)}
                              onDeclinar={() => handleDeclinarInteresse(m)} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}
      </div>

      {/* Right panel — desktop only */}
      <div className="hidden lg:flex flex-col overflow-y-auto border-l border-line-soft" style={{width: 400, scrollbarWidth: 'none', background: '#F6F5FB', minHeight: '100vh'}}>
        {modalDetalhe ? (
          <div style={{padding: '34px 30px 40px'}}>
            <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 10}}>
              <button onClick={() => setModalDetalhe(null)}
                style={{display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: '#9A93A4', fontSize: 13, fontWeight: 600, padding: 0}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Capa centralizada */}
            <div style={{width: 172, margin: '0 auto 20px', borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 32px rgba(33,28,43,0.22)', aspectRatio: '2/3', background: '#E8E4F0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              {modalDetalhe.capa_url
                ? <img src={modalDetalhe.capa_url} alt="" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                : <BookOpen size={36} color="#C4BCDA" strokeWidth={1} />}
            </div>

            {/* Título + autor centralizados */}
            <div style={{textAlign: 'center', marginBottom: 4}}>
              <h2 style={{fontSize: 24, fontWeight: 800, letterSpacing: '-.02em', margin: 0, lineHeight: 1.1, color: '#211C2B', fontFamily: 'Newsreader,Georgia,serif'}}>{modalDetalhe.titulo}</h2>
              <p style={{color: '#6E6878', margin: '5px 0 0', fontSize: 15}}>{modalDetalhe.autor}</p>
            </div>

            {/* Badge status */}
            <div style={{display: 'flex', justifyContent: 'center', margin: '14px 0 0'}}>
              <span style={{display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 13px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, background: '#E4F6EE', color: '#1FA971'}}>
                <span style={{width: 7, height: 7, borderRadius: '50%', background: '#1FA971'}} />
                Disponível para empréstimo
              </span>
            </div>

            {/* Botões */}
            <div style={{display: 'flex', flexDirection: 'column', gap: 10, margin: '18px 0'}}>
              <button
                onClick={() => { setModalDetalhe(null); setModalSolicitacao(modalDetalhe); }}
                style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '13px', borderRadius: 999, background: '#5B3CE4', color: '#fff', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 6px 16px rgba(91,60,228,.30)'}}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                Solicitar empréstimo
              </button>
              <button
                onClick={() => { setModalDetalhe(null); setTimeout(() => acaoCard('right'), 30); }}
                style={{width: '100%', padding: '13px', borderRadius: 999, fontSize: 14, fontWeight: 700, border: '1px solid #E4E0F0', color: '#211C2B', background: '#fff', cursor: 'pointer'}}>
                Salvar para depois
              </button>
            </div>

            {/* Meta */}
            <div style={{borderTop: '1px solid #E4E0F0'}}>
              {modalDetalhe.genero && (
                <div style={{display: 'flex', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid #E4E0F0', fontSize: 14}}>
                  <span style={{color: '#6E6878'}}>Gênero</span>
                  <b style={{fontWeight: 600, color: '#211C2B'}}>{modalDetalhe.genero}</b>
                </div>
              )}
              <div style={{display: 'flex', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid #E4E0F0', fontSize: 14}}>
                <span style={{color: '#6E6878'}}>Dono</span>
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                  <AvatarPremium fotoUrl={modalDetalhe.perfil?.foto_url} nome={modalDetalhe.perfil?.nome} isPremium={modalDetalhe.perfil?.plano === 'pago'} size={20} />
                  <b style={{fontWeight: 600, color: '#211C2B'}}>{modalDetalhe.perfil?.nome?.split(' ')[0]}</b>
                </div>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid #E4E0F0', fontSize: 14}}>
                <span style={{color: '#6E6878'}}>Distância</span>
                <b style={{fontWeight: 600, color: '#211C2B'}}>{formatarDist(modalDetalhe.perfil?.distancia || 0)}</b>
              </div>
              {modalDetalhe.estrelas > 0 && (
                <div style={{display: 'flex', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid #E4E0F0', fontSize: 14}}>
                  <span style={{color: '#6E6878'}}>Avaliação</span>
                  <span style={{color: '#E0A02E', fontSize: 15}}>{'★'.repeat(modalDetalhe.estrelas)}{'☆'.repeat(5 - modalDetalhe.estrelas)}</span>
                </div>
              )}
            </div>

            {/* Sinopse */}
            {modalDetalhe.descricao && (
              <p style={{fontSize: 14, color: '#3D3748', lineHeight: 1.6, margin: '14px 0 0'}}>
                {modalDetalhe.descricao}
              </p>
            )}
          </div>
        ) : (
          <div style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, opacity: 0.45}}>
            <BookOpen size={36} strokeWidth={1.2} color="#9A93A4" />
            <p style={{fontSize: 13, color: '#9A93A4', marginTop: 14, textAlign: 'center', lineHeight: 1.5}}>Selecione um livro para ver os detalhes</p>
          </div>
        )}
      </div>

      {/* Modal: detalhe do livro — mobile only */}
      {modalDetalhe && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: 'rgba(33,28,43,0.52)' }}
          onClick={() => setModalDetalhe(null)}>
          <div className="bg-white w-full max-w-md rounded-t-[28px] pb-safe overflow-hidden"
            onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-line mx-auto mt-4 mb-4" />

            {/* Dono */}
            <div className="flex items-center gap-3 px-5 py-3 mx-5 mb-4 rounded-[14px]"
              style={{ background: '#F7F5FC' }}>
              <AvatarPremium
                fotoUrl={modalDetalhe.perfil?.foto_url}
                nome={modalDetalhe.perfil?.nome}
                isPremium={modalDetalhe.perfil?.plano === 'pago'}
                size={36} />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-ink-900 truncate">{modalDetalhe.perfil?.nome}</p>
                <p className="text-[11px] text-ink-400 font-medium">
                  {formatarDist(modalDetalhe.perfil?.distancia || 0)} de distância
                </p>
              </div>
            </div>

            {/* Capa + info */}
            <div className="flex gap-4 px-5 mb-4">
              <div className="w-[72px] h-[100px] rounded-[12px] overflow-hidden flex-shrink-0 flex items-center justify-center"
                style={{ background: '#E8E4F0' }}>
                {modalDetalhe.capa_url
                  ? <img src={modalDetalhe.capa_url} alt="" className="w-full h-full object-cover" />
                  : <BookOpen size={28} color="#C4BCDA" strokeWidth={1} />}
              </div>
              <div className="flex-1 min-w-0 pt-1">
                <p className="text-[17px] font-bold text-ink-900 leading-snug mb-1"
                  style={{ fontFamily: "'Newsreader', Georgia, serif" }}>
                  {modalDetalhe.titulo}
                </p>
                <p className="text-[13px] text-ink-400 font-medium mb-2 truncate">{modalDetalhe.autor}</p>
                {modalDetalhe.estrelas > 0 && (
                  <p className="text-[13px] text-amber-400">
                    {'★'.repeat(modalDetalhe.estrelas)}{'☆'.repeat(5 - modalDetalhe.estrelas)}
                  </p>
                )}
              </div>
            </div>

            {/* Descrição */}
            {modalDetalhe.descricao && (
              <p className="text-[13px] text-ink-500 leading-relaxed px-5 mb-5 line-clamp-3">
                {modalDetalhe.descricao}
              </p>
            )}

            {/* Ações */}
            <div className="px-5 pb-6 flex flex-col gap-2">
              <button
                onClick={() => { setModalDetalhe(null); setModalSolicitacao(modalDetalhe); }}
                className="w-full py-3 rounded-[14px] text-[14px] font-bold text-white"
                style={{ background: '#5B3CE4' }}>
                Quero pedir emprestado ↑
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => { setModalDetalhe(null); setTimeout(() => acaoCard('right'), 30); }}
                  className="flex-1 py-3 rounded-[14px] text-[13px] font-bold border"
                  style={{ borderColor: '#CFC5F7', color: '#5B3CE4', background: '#EDE8FC' }}>
                  Guardar para depois
                </button>
                <button
                  onClick={() => { setModalDetalhe(null); setTimeout(() => acaoCard('left'), 30); }}
                  className="flex-1 py-3 rounded-[14px] text-[13px] font-bold border"
                  style={{ borderColor: '#EBE8F0', color: '#9A93A4', background: 'white' }}>
                  Passar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de solicitação de empréstimo */}
      {modalSolicitacao && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center px-0 lg:px-4" style={{background:'rgba(0,0,0,0.4)'}}>
          <div className="bg-white w-full max-w-md rounded-t-2xl lg:rounded-2xl p-5 max-h-[90vh] overflow-y-auto lg:shadow-2xl">
            {sucessoSolicitacao ? (
              <div className="text-center py-6">
                <CheckCircle size={40} color="#1FA971" strokeWidth={1.5} className="mb-3" />
                <p className="text-base font-medium text-ink-900 mb-1">Solicitação enviada!</p>
                <p className="text-sm text-ink-400 mb-5">Aguarde o dono aprovar o empréstimo.</p>
                <button onClick={() => { setModalSolicitacao(null); setSucessoSolicitacao(false); }}
                  className="w-full py-3 text-sm border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">
                  Fechar
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-medium text-ink-900">Solicitar empréstimo</p>
                  <button onClick={() => { setModalSolicitacao(null); setDragX(0); setDragY(0); }} className="text-ink-400"><X size={18} /></button>
                </div>

                <div className="flex gap-3 pb-4 mb-4 border-b border-gray-50">
                  <div className="w-10 h-14 bg-paper rounded flex items-center justify-center text-xl flex-shrink-0 overflow-hidden">
                    {modalSolicitacao.capa_url
                      ? <img src={modalSolicitacao.capa_url} alt="" className="w-full h-full object-cover" />
                      : <BookOpen size={20} color="#B8B0CC" strokeWidth={1.5} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-900 leading-tight">{modalSolicitacao.titulo}</p>
                    <p className="text-xs text-ink-400 mt-0.5">{modalSolicitacao.autor}</p>
                  </div>
                </div>

                <div className="bg-paper rounded-xl px-4 py-3 mb-4">
                  <p className="text-xs text-ink-400">Solicitando como</p>
                  <p className="text-sm font-medium text-ink-900 mt-0.5">{nomeUsuario}</p>
                </div>

                <div className="mb-4">
                  <label className="text-xs text-ink-400 mb-1 block">Prazo desejado</label>
                  <select value={prazo} onChange={e => setPrazo(parseInt(e.target.value))}
                    className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm bg-paper outline-none focus:border-line">
                    <option value={7}>1 semana</option>
                    <option value={14}>2 semanas</option>
                    <option value={21}>3 semanas</option>
                    <option value={30}>1 mês</option>
                    <option value={0}>Sem prazo definido</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="text-xs text-ink-400 mb-1 block">Mensagem <span className="text-ink-300">(opcional)</span></label>
                  <textarea
                    placeholder="Oi! Posso pegar emprestado?"
                    value={msgSolicitacao}
                    onChange={e => setMsgSolicitacao(e.target.value)}
                    rows={3}
                    className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm bg-paper outline-none focus:border-line resize-none"
                  />
                </div>

                <div className="flex gap-2">
                  <button onClick={confirmarSolicitacao} disabled={enviando}
                    className="flex-1 py-3 text-sm bg-brand text-white rounded-xl hover:bg-brand-dark transition-colors disabled:opacity-50">
                    {enviando ? 'Enviando...' : 'Solicitar empréstimo'}
                  </button>
                  <button onClick={() => { setModalSolicitacao(null); setDragX(0); setDragY(0); }}
                    className="flex-1 py-3 text-sm border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">
                    Cancelar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bottom nav — mobile only */}
      <div className="fixed bottom-0 left-0 right-0 z-30 flex items-center px-[18px] lg:hidden"
        style={{ paddingTop: 10, paddingBottom: 'calc(10px + env(safe-area-inset-bottom))', ...frosted }}>
        <button onClick={() => setTela('acervo')}
          className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#9A93A4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0" /><path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0" />
            <line x1="3" y1="6" x2="3" y2="19" /><line x1="12" y1="6" x2="12" y2="19" /><line x1="21" y1="6" x2="21" y2="19" />
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Estante</span>
        </button>
        <button className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#5B3CE4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Explorar</span>
        </button>
        <div className="flex-none mx-1.5">
          <button onClick={() => setTela('cadastrar')}
            className="w-[52px] h-[52px] rounded-[17px] border-0 cursor-pointer text-white flex items-center justify-center"
            style={{ background: 'linear-gradient(150deg,#6E50F0,#3F27B0)', marginTop: -18, boxShadow: '0 8px 18px rgba(91,60,228,0.40)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
        <button onClick={() => setTela('amigos')}
          className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#9A93A4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
            <polyline points="16 11 18 13 22 9" />
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Amigos</span>
        </button>
        <button onClick={() => setTela('clubes')}
          className="flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer"
          style={{ color: '#9A93A4' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <span className="text-[10px] font-semibold tracking-[0.01em]">Clubes</span>
        </button>
      </div>
    </div>
  );
}
